import axios from "axios";
import { toast } from "sonner";
import { baseUrl } from "utils/baseUrl";
import { getToken } from "utils/localStorageServices";

const safeLocalStorageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures in restricted iframe contexts.
  }
};

const safeLocalStorageClear = () => {
  try {
    localStorage.clear();
  } catch {
    // Ignore storage clear failures in restricted iframe contexts.
  }
};

const buildLoginRedirectUrl = () => {
  try {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    const query = new URLSearchParams(window.location.search);
    const isMeetRoute = window.location.pathname.startsWith("/meet-addon/");
    const hasMeetQuery =
      query.has("meet_sdk") ||
      query.get("meetAddon") === "true" ||
      query.get("source") === "meet-addon";

    if (isMeetRoute || hasMeetQuery) {
      return `/login?redirect=${encodeURIComponent(currentPath)}`;
    }
  } catch {
    // Fall back below.
  }

  return "/login";
};

const instance = axios.create({
  baseURL: baseUrl,
  headers: {
    // Accept: "application/json",
    // "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  // timeout: 1000,
});

instance.interceptors.request.use(
  (config) => {
    // Do something before request is sent
    if (getToken()) {
      config.headers["Authorization"] = `Bearer ${getToken()}`;
    }
    return config;
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  (error) => {
    const cfg = error?.config;
    const failedUrl = cfg?.url;
    const errorStatus = error?.response?.status;
    const errorList = error?.response?.data?.errors;
    const requestMsg =
      error?.response?.data?.message ??
      error?.response?.data?.title ??
      error?.message;
    const isViewerMode = window.location.href.includes("viewer-mode");
    const isLogoutRequest = failedUrl
      ?.toLowerCase()
      .includes("/account/logout");

    // Backend uses 400 for business states (like "Speaker not found").
    // We calculate shouldSuppressToast to avoid showing a toast for expected errors,
    // while still showing toasts for other errors.
    const shouldSuppressToast =
      cfg?.suppressToast ||
      cfg?.suppressStatuses?.includes(errorStatus) ||
      cfg?.suppressMessages?.includes(requestMsg);

    // NEW: Handle suppressed errors as successful responses
    if (shouldSuppressToast && cfg?.returnEmptyOnSuppress) {
      return {
        data: undefined, // or cfg?.emptyValue ?? undefined
        status: 200,
        statusText: "OK",
        headers: error?.response?.headers || {},
        config: cfg,
      };
    }

    // Network error
    if (
      !errorStatus &&
      requestMsg === "Network Error" &&
      !failedUrl?.includes("/Document/stream/")
    ) {
      if (cfg.showToast) {
        toast.error("Unexpected error");
      }
      return Promise.reject(error);
    }

    // Unauthorized
    if (errorStatus === 401 && !isViewerMode && !isLogoutRequest) {
      toast.error("Session Expired");
      safeLocalStorageClear();
      const currentUrl = window.location.href;
      const loginUrl = buildLoginRedirectUrl();
      safeLocalStorageSet("previousPath", window.location.pathname);
      if (currentUrl.toLowerCase().includes("external")) {
        const newTabUrl = `${window.location.origin}${loginUrl}`;
        // Try opening the new tab
        const newTab = window.open(newTabUrl, "_blank");
        // Check if the pop-up was blocked
        if (!newTab) {
          safeLocalStorageSet("401Url", failedUrl ?? "");
          window.location.href = loginUrl;
        }
      } else if (currentUrl.includes("event-details")) {
        safeLocalStorageSet("redirect-after-otp", currentUrl);
        window.location.href = "/login-with-otp";
      } else {
        safeLocalStorageSet("401Url", failedUrl ?? "");
        window.location.href = loginUrl;
      }
      return Promise.reject(error);
    }

    // Viewer mode 401
    if (isViewerMode && errorStatus === 401) {
      if (cfg.showToast) {
        toast.error(requestMsg);
      }
      return Promise.reject(error);
    }

    // Validation errors
    if (requestMsg === "One or more validation errors occurred." && errorList) {
      Object.keys(errorList).forEach((key) => {
        if (cfg.showToast) {
          toast.error(errorList[key][0]);
        }
      });
    }
    // Generic error
    else if (!failedUrl?.includes("/Document/stream/")) {
      if (cfg.showToast) {
        toast.error(requestMsg);
      }
    }

    if (failedUrl) {
      safeLocalStorageSet("401Url", failedUrl);
    }
    return Promise.reject(error);
  },
);

export default instance;
